/**
 * @file 预约按钮状态工具函数
 * @description 根据用户预约次数和设备空闲情况计算按钮状态
 */

import { EquipmentOverview } from "../components/BookingStationItem";

/**
 * VIP 类型枚举
 * 1 - 小鹏车主高压会员
 * 2 - 普通会员
 * 3 - 非会员
 * 0 - 拿不到身份
 */
export enum VipTypeEnum {
  Unknown = 0,
  HighVoltageOwner = 1,
  NormalMember = 2,
  NonMember = 3,
}

/**
 * VIP 类型文案映射
 */
export const VipTypeTextMap: Record<VipTypeEnum, string> = {
  [VipTypeEnum.Unknown]: "拿不到身份",
  [VipTypeEnum.HighVoltageOwner]: "小鹏车主高压会员",
  [VipTypeEnum.NormalMember]: "普通会员",
  [VipTypeEnum.NonMember]: "非会员",
};

/**
 * 获取 VIP 类型
 * @param memberTypeSeg 会员身份(细分): 1-高压车主会员, 2-普通车主会员, 3-非车主会员
 * @param memberType 会员开通身份: 0-非车主开通, 1-车主开通
 * @param member 是否会员: 0-非会员, 1-会员
 * @returns VipTypeEnum
 */
export const getVipType = (
  memberTypeSeg?: number,
  member?: number
): VipTypeEnum => {
  // 先判断是否会员
  if (member === 0) {
    return VipTypeEnum.NonMember; // 非会员
  }

  // 优先使用 memberTypeSeg 细分字段
  if (memberTypeSeg === 1) {
    return VipTypeEnum.HighVoltageOwner; // 高压车主会员
  }
  if (memberTypeSeg === 2 || memberTypeSeg === 3) {
    return VipTypeEnum.NormalMember; // 普通车主会员 或 非车主会员 都算普通会员
  }

  return VipTypeEnum.Unknown; // 拿不到身份
};

/**
 * 预约按钮状态接口
 */
export interface BookingButtonState {
  /** 是否禁用 */
  disabled: boolean;
  /** 禁用原因 */
  disabledReason: string;
  /** 禁用时显示的文本 */
  disabledText: string;
}

/**
 * 检查是否有可用的充电桩
 * 需求: 4.4 - 当充电站无可用充电桩时，禁用预约按钮
 * @param equipList 设备列表
 * @returns 是否有可用充电桩
 */
export const hasAvailablePile = (equipList: EquipmentOverview[]): boolean => {
  if (!equipList || equipList.length === 0) {
    return false;
  }
  // 检查是否有任何设备有空闲数量
  return equipList.some((equip) => equip.freeNums > 0);
};

/**
 * 检查用户是否还有预约次数
 * 需求: 4.3 - 当用户预约次数已用完时，禁用预约按钮
 * @param remainingCount 剩余预约次数，-1 表示无限制
 * @returns 是否还有预约次数
 */
export const hasRemainingBookingQuota = (remainingCount: number): boolean => {
  // -1 表示无限制
  if (remainingCount === -1) {
    return true;
  }
  return remainingCount > 0;
};

/**
 * 获取预约按钮状态
 * 根据剩余预约次数和设备空闲数量判断按钮是否禁用
 * 需求: 4.3, 4.4
 * @param remainingCount 剩余预约次数，-1 表示无限制
 * @param equipList 设备列表
 * @returns 按钮状态
 */
export const getBookingButtonState = (
  remainingCount: number,
  equipList: EquipmentOverview[]
): BookingButtonState => {
  // 检查预约次数
  if (!hasRemainingBookingQuota(remainingCount)) {
    return {
      disabled: true,
      disabledReason: "quota_exhausted",
      disabledText: "次数已用完",
    };
  }

  // 检查设备可用性
  if (!hasAvailablePile(equipList)) {
    return {
      disabled: true,
      disabledReason: "no_available_pile",
      disabledText: "暂无空闲",
    };
  }

  // 可以预约
  return {
    disabled: false,
    disabledReason: "",
    disabledText: "",
  };
};


